//LIBRARY

import styled, { css, keyframes } from "styled-components";

//MISC

import { ButtonType, IconProps } from "./ButtonAtomModel";
import {
  COLORS,
  MARGIN,
  DEVICE_SIZE,
  BORDER_RADIUS,
} from "constants/variables";

//ASSET

import cursorIcon from "assets/img/Cursor.png";

const arrowDownAnimation = keyframes`
  from {
    transform: rotate(90deg) translateX(0);
  }
  to {
    transform: rotate(90deg) translateX(4px);
  }
`;

const arrowRightAnimation = keyframes`
  from {
    transform: translateX(0px);
  }
  to {
    transform: translateX(4px);
  }
`;

export const Icon = styled.img<IconProps>`
  transform: ${({ arrowDown, type }) =>
    arrowDown || type === ButtonType.DownloadButton ? "rotate(90deg)" : "none"};
  width: ${({ type }) =>
    type === ButtonType.ArrowButton
      ? "12px"
      : type === ButtonType.ChatButton
      ? "18px"
      : "16px"};
  height: ${({ type }) =>
    type === ButtonType.ArrowButton
      ? "12px"
      : type === ButtonType.ChatButton
      ? "18px"
      : "18px"};
  margin: 0 5px;
`;

export const Button = styled.button<{
  type?: keyof typeof ButtonType;
  biggerCursor?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${MARGIN.max10} ${MARGIN.fix16};
  border: none;
  border-radius: ${BORDER_RADIUS.radius21};
  transition: 0.3s background-color ease-out;
  &:hover {
    background-color: ${COLORS.Background};
    transition: 0.3s background-color ease-out;
  }
  cursor: ${({ biggerCursor }) =>
    biggerCursor ? `url(${cursorIcon}), auto` : `pointer`};

  ${({ type }) =>
    type === ButtonType.X &&
    css`
      background-color: ${COLORS.Cultured};
      border-radius: ${BORDER_RADIUS.radius24};
      aspect-ratio: 1/1;
      height: 48px;
      width: 48px;
      &:hover {
        background-color: ${COLORS.MetalicSilver}!important;
        transition: 0.5s ease-in;
      }
    `}

  ${({ type }) =>
    type === ButtonType.MapActive &&
    css`
      background-color: ${COLORS.VividAuburn};
      &:hover {
        background-color: ${COLORS.VividAuburn}!important;
        transition: 0.5s ease-in;
      }
    `}

  ${({ type }) =>
    type === ButtonType.Audio &&
    css`
      background-color: ${COLORS.Cultured};
      &:hover {
        background-color: ${COLORS.ChineseSilver};
        transition: 0.3s ease-in;
      }
    `}

  ${({ type }) =>
    type === ButtonType.ArrowButton &&
    css`
      background-color: ${COLORS.Background};
      &:hover {
        background-color: ${COLORS.ChineseSilver};
        transition: 0.3s ease-in;

        ${Icon} {
          animation: ${arrowRightAnimation} 0.3s forwards;
        }
      }
    `}

  ${({ type }) =>
    type === ButtonType.ArrowDownRed &&
    css`
      background-color: ${COLORS.VividAuburn};
      &:hover {
        background-color: ${COLORS.CrimsonFire};
        transition: 0.3s ease-in;

        ${Icon} {
          animation: ${arrowDownAnimation} 0.3s forwards;
        }
      }
    `}

    ${({ type }) =>
    type === ButtonType.CookieButton &&
    css`
      background-color: ${COLORS.VividAuburn};
      &:hover {
        background-color: ${COLORS.CrimsonFire};
        transition: 0.3s ease-in;

        ${Icon} {
          animation: ${arrowDownAnimation} 0.3s forwards;
        }
      }
    `}

    ${({ type }) =>
    type === ButtonType.ManagementButton &&
    css`
      background-color: ${COLORS.Gunmetal};
      &:hover {
        background-color: ${COLORS.lineColor};
        transition: 0.3s ease-in;

        ${Icon} {
          animation: ${arrowDownAnimation} 0.3s forwards;
        }
      }
    `}

  ${({ type }) =>
    type === ButtonType.ArrowDown &&
    css`
      background-color: ${COLORS.FaluRed};
      &:hover {
        background-color: ${COLORS.ChineseSilver};
        transition: 0.3s ease-in;

        ${Icon} {
          animation: ${arrowDownAnimation} 0.3s forwards;
        }
      }
    `}

  ${({ type }) =>
    type === ButtonType.RedButton &&
    css`
      width: 100%;
      height: 100%;
      background-color: ${COLORS.VividAuburn};
      white-space: nowrap;
      &:hover {
        background-color: ${COLORS.CrimsonFire};
        transition: 0.3s ease-in;
        ${Icon} {
          animation: ${arrowRightAnimation} 0.3s forwards;
        }
      }
    `}

  ${({ type }) =>
    type === ButtonType.ChatButton &&
    css`
      margin-right: -10px;
      padding: 10px;
    `}

  ${({ type }) =>
    type === ButtonType.Link &&
    css`
      background-color: ${COLORS.ChineseSilver};
      padding: ${MARGIN.max8} ${MARGIN.max12};
      @media (max-width: ${DEVICE_SIZE.MOBILE}) {
        padding: 8px;
      }
      &:hover {
        background-color: ${COLORS.MetalicSilver};
        transition: 0.3s ease-in;

        ${Icon} {
          animation: ${arrowRightAnimation} 0.3s infinite alternate;
        }
      }
    `};

  &:disabled {
    background-color: ${COLORS.MetalicSilver};
    color: ${COLORS.Cultured};
    cursor: not-allowed;
  }

  ${({ type }) =>
    type === ButtonType.RedButtonWhiteIcon &&
    css`
      background-color: ${COLORS.VividAuburn};
      &:hover {
        background-color: ${COLORS.CrimsonFire};
        transition: 0.3s ease-in;

        ${Icon} {
          animation: ${arrowRightAnimation} 0.3s infinite alternate;
        }
      }
    `}
  ${({ type }) =>
    type === ButtonType.BlackButton &&
    css`
      background-color: ${COLORS.RaisinBlack};
      padding: ${MARGIN.max8} ${MARGIN.fix16};

      &:hover {
        background-color: rgba(29, 29, 38, 0.4);
        transition: background 0.3s ease-in-out;
      }
    `};

  &:disabled {
    background-color: ${COLORS.MetalicSilver};
    color: ${COLORS.Cultured};
    cursor: not-allowed;
  }
`;
